// SiegerBuehne.jsx — Siegerehrung im Turniertag-Bild (20.10.2026)
//
// Vorlage: docs/muster-animationen/muster.html, Seite „Siegerehrung“ (vom
// Veranstalter abgenommen). Hintergrund /turniertag/buehne.jpg, Podest 2-1-3
// wächst aus dem Boden, dann 3 → 2 → Spannung → 1 mit Blitz, Strahlen,
// „Sieger“ und goldenen Funken; rechts die Ergebnisliste, danach „Weitere
// Plätze“ ab 4 klein.
//
// Ein Dritter oder zwei: das entscheiden die Daten. Stehen zwei Plätze 3 in
// der Kategorie (geteilte Dritte, Doppel-Bronze), stehen beide auf dem
// Podest. Die Plätze ab 4 kommen aus derselben Liste (bracket_teilnehmer /
// formen_teilnehmer.platzierung, im K.-o. vergeben von utils/koPlatzierung.js).
import React, { useLayoutEffect, useRef } from 'react';
import './SiegerBuehne.css';
import { TEMPO_EHRUNG, animiere, warte, Medaille, starterBild, bildUrl, punkteText, ohneBewegung } from './kataEhrungAnim';

const T = TEMPO_EHRUNG;
// Dauer des ganzen Ablaufs bis „Weitere Plätze“ stehen (Podest, 3, 2,
// Spannung, Sieger, Weitere Plätze). Die Formen-Leinwand lässt das Bild
// danach noch rund 10 s stehen, bevor sie die nächste Kategorie ankündigt.
export const SIEGER_ABLAUF_MS = Math.round((1100 + 1500 + 1700 + 900 + 500 + 1800) * T + 1200);
const name = (p) => [p?.vorname, p?.nachname].filter(Boolean).join(' ');

// Funken: fester Zufall wie im Muster (kein Ton, keine Emoji)
const FUNKEN = (() => {
  let z = 7;
  const zuf = () => { z = (z * 9301 + 49297) % 233280; return z / 233280; };
  return Array.from({ length: 70 }, (_, i) => {
    const farbe = ['#ffe9a6', '#e8c25a', '#fff6cf', '#c8922c'][i % 4];
    const links = zuf() * 100;
    const fall = 70 + zuf() * 40, dreh = zuf() * 720 - 360, seit = (zuf() - 0.5) * 10;
    const dauer = 2600 + zuf() * 1800, verz = zuf() * 1600;
    return { farbe, links, fall, dreh, seit, dauer, verz };
  });
})();

// Lage der Karten über den Podesten (Mitte in % der Breite, Unterkante in %
// der Höhe: Podest-Unterkante 6 % plus Podesthöhe).
const KARTE = {
  1: { mitte: 34.5, unten: 36, breite: 19 },
  2: { mitte: 16, unten: 27, breite: 14 },
  3: { mitte: 52.75, unten: 21, breite: 14 },
};

// Namen auf dem Podest einpassen (21.10.2026): der Siegername wird nie
// gekürzt, nur kleiner (bis auf 60 %), Umbruch nur zwischen Vor- und Nachname.
// Die schmalen Karten zweier Dritter und die Ergebnisliste rechts bleiben
// einzeilig und werden ebenso kleiner, bevor „…“ kommt. Schriftgröße in cqw,
// damit sie mit der Bühne skaliert.
function namenEinpassen(eh) {
  const cqw = eh.clientWidth / 100;
  if (!(cqw > 0)) return;
  eh.querySelectorAll('.sb-karte.platz1 .sb-en, .sb-karte.klein .sb-en, .sb-ez .sb-n').forEach((el) => {
    el.style.fontSize = '';
    const mw = getComputedStyle(el).maxWidth;
    const max = mw && mw !== 'none' ? parseFloat(mw) : el.clientWidth;
    const basis = parseFloat(getComputedStyle(el).fontSize);
    if (!(max > 0) || !(basis > 0)) return;
    const teile = el.closest('.platz1') ? [...el.children] : [el];
    const breite = () => Math.max(...teile.map((t) => (t === el ? t.scrollWidth : t.offsetWidth)));
    let f = basis;
    while (breite() > max + 0.5 && f > basis * 0.6) {
      f *= 0.95;
      el.style.fontSize = `${(f / cqw).toFixed(3)}cqw`;
    }
  });
}

// halle = { turnierId, k } des Bildschirms: nur damit Fotos (21.10.2026)
export default function SiegerBuehne({ kategorie, idx, total, halle = null }) {
  const plaetze = (kategorie?.plaetze || []).slice().sort((a, b) => a.platzierung - b.platzierung);
  const erster = plaetze.find((p) => p.platzierung === 1) || null;
  const zweiter = plaetze.find((p) => p.platzierung === 2) || null;
  const dritte = plaetze.filter((p) => p.platzierung === 3).slice(0, 2);
  const zweiDritte = dritte.length === 2;
  const rest = plaetze.filter((p) => p.platzierung > 3);
  const podium = [erster, zweiter, ...dritte].filter(Boolean);

  // Weitere Plätze: so viele, wie neben bzw. über den Tiger passen. Die linke
  // Spalte darf tiefer laufen (bis 76 % der Höhe), die rechte endet über dem
  // Kopf des Tigers (61 %). Werte in % der Bühnenhöhe, im Bildschirmfoto
  // nachgemessen.
  const restStart = 24 + podium.length * (zweiDritte ? 7.9 : 9.8) + 3;
  const links = Math.max(1, Math.floor((76 - restStart) / 3.6));
  const rechts = Math.max(0, Math.floor((61 - restStart) / 3.6));
  const passt = links + rechts;
  const restZeigen = rest.length > passt ? rest.slice(0, passt - 1) : rest;
  const restMehr = rest.length - restZeigen.length;
  const spalteL = restZeigen.slice(0, links);
  const spalteR = restZeigen.slice(links);

  const buehneRef = useRef(null);

  useLayoutEffect(() => {
    const eh = buehneRef.current;
    if (!eh) return undefined;
    let aus = false;
    namenEinpassen(eh);
    // Webfont/Ersatzschrift kann später kommen: dann noch einmal messen
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (!aus && buehneRef.current) namenEinpassen(buehneRef.current); });
    return () => { aus = true; };
  }, [kategorie]);

  useLayoutEffect(() => {
    const eh = buehneRef.current;
    if (!eh) return undefined;
    if (ohneBewegung()) { eh.classList.add('sb-fertig'); return undefined; }
    let aus = false;
    const weiter = () => !aus && !!buehneRef.current;
    const q = (s) => eh.querySelector(s);
    const qa = (s) => [...eh.querySelectorAll(s)];
    const hoch = (el, verz) => animiere(el, [{ transform: 'translateY(110%)' }, { transform: 'translateY(-3%)', offset: 0.8 }, { transform: 'translateY(0)' }],
      { duration: 650, delay: verz, easing: 'cubic-bezier(.3,.8,.25,1)', fill: 'both' }, T);
    const auftritt = (k, gross) => animiere(k, [
      { opacity: 0, transform: `translateY(${gross ? 8 : 5}cqw) scale(.6)`, filter: 'brightness(2.2) blur(4px)' },
      { opacity: 1, transform: 'translateY(-1cqw) scale(1.08)', filter: 'brightness(1.4) blur(0)', offset: 0.6 },
      { opacity: 1, transform: 'translateY(0) scale(1)', filter: 'brightness(1)' },
    ], { duration: gross ? 900 : 700, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'both' }, T);
    const zeile = (z) => animiere(z, [{ opacity: 0, transform: 'translateX(20%)' }, { opacity: 1, transform: 'none' }],
      { duration: 600, delay: 300, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' }, T);
    const glanz = (k) => {
      const eb = k && k.querySelector('.sb-eb');
      if (!eb) return;
      const g = document.createElement('div');
      g.className = 'kea-glanz';
      eb.appendChild(g);
      animiere(g, [{ left: '-40%' }, { left: '130%' }], { duration: 600, easing: 'ease-in-out', fill: 'both' }, T);
    };
    const zeitgeber = [];
    const spaeter = (fn, ms) => zeitgeber.push(setTimeout(() => { if (weiter()) fn(); }, ms * T));

    // 1. Podest wächst aus dem Boden (3, 2, 1)
    hoch(q('.sb-podest.p3'), 0); hoch(q('.sb-podest.p2'), 140); hoch(q('.sb-podest.p1'), 280);
    (async () => {
      await warte(1100, T); if (!weiter()) return;
      // 2. Dritte(r)
      qa('.sb-karte.platz3').forEach((k) => { auftritt(k, false); spaeter(() => glanz(k), 500); });
      qa('.sb-ez[data-p="3"]').forEach(zeile);
      await warte(1500, T); if (!weiter()) return;
      // 3. Zweiter
      const k2 = q('.sb-karte.platz2');
      if (k2) { auftritt(k2, false); spaeter(() => glanz(k2), 500); }
      qa('.sb-ez[data-p="2"]').forEach(zeile);
      await warte(1700, T); if (!weiter()) return;
      // 4. Spannung: Saal dunkler, Licht auf die Mitte
      qa('.sb-karte').forEach((k) => animiere(k, [{ filter: 'brightness(1)' }, { filter: 'brightness(.55)' }], { duration: 500, fill: 'forwards' }, T));
      animiere(q('.sb-licht'), [{ opacity: 0 }, { opacity: 1 }], { duration: 700, fill: 'forwards' }, T);
      await warte(900, T); if (!weiter()) return;
      // 5. Sieger: Blitz, Karte steigt auf, Strahlen, Funken
      const k1 = q('.sb-karte.platz1');
      const b = q('.sb-blitz-gross');
      animiere(b, [{ transform: 'scale(.2)', opacity: 1 }, { transform: 'scale(1.3)', opacity: 0 }], { duration: 900, easing: 'ease-out', fill: 'both' }, T);
      if (k1) auftritt(k1, true);
      qa('.sb-ez[data-p="1"]').forEach(zeile);
      animiere(q('.sb-strahlen'), [
        { opacity: 0, transform: 'translate(-50%,-50%) rotate(0deg) scale(.5)' },
        { opacity: 1, transform: 'translate(-50%,-50%) rotate(40deg) scale(1)' },
      ], { duration: 3200, easing: 'ease-out', fill: 'forwards' }, T);
      qa('.sb-karte:not(.platz1)').forEach((k) => animiere(k, [{ filter: 'brightness(.55)' }, { filter: 'brightness(1)' }], { duration: 900, delay: 600, fill: 'forwards' }, T));
      qa('.sb-funke').forEach((f, i) => {
        const d = FUNKEN[i];
        animiere(f, [
          { opacity: 0, transform: 'translateY(0) rotate(0)' }, { opacity: 1, offset: 0.1 }, { opacity: 1, offset: 0.8 },
          { opacity: 0, transform: `translate(${d.seit}cqw, ${d.fall}cqh) rotate(${d.dreh}deg)` },
        ], { duration: d.dauer, delay: d.verz, easing: 'cubic-bezier(.3,.1,.7,1)', fill: 'both' }, T);
      });
      await warte(500, T); if (!weiter()) return;
      // 6. „Sieger“ und Glanz über dem Siegerbild
      if (k1) {
        animiere(q('.sb-sieger'), [
          { opacity: 0, transform: 'translateX(-80%) skewX(-20deg) scale(1.5)', filter: 'blur(8px)' },
          { opacity: 1, transform: 'translateX(-50%) skewX(-6deg) scale(1)', filter: 'blur(0) drop-shadow(0 0 .3em rgba(255,200,90,.6))', offset: 0.6 },
          { opacity: 1, transform: 'translateX(-50%) skewX(-6deg) scale(1)' },
        ], { duration: 800, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' }, T);
        glanz(k1);
      }
      // 7. Weitere Plätze, klein unter der Ergebnisliste (ohne Tempofaktor wie im Muster)
      spaeter(() => {
        const titel = q('.sb-rest-titel');
        if (titel) titel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 400, fill: 'forwards' });
        qa('.sb-er').forEach((z, i) => z.animate([{ opacity: 0, transform: 'translateY(30%)' }, { opacity: 0.9, transform: 'none' }],
          { duration: 400, delay: 200 + i * 120, easing: 'ease-out', fill: 'forwards' }));
      }, 1800);
    })();
    return () => { aus = true; zeitgeber.forEach(clearTimeout); };
  }, []);

  const karte = (p, platz, links, unten, breite, klein) => {
    const bild = starterBild(p, null, halle);
    return (
      <div key={`${platz}-${links}`} className={`sb-karte platz${platz} ${klein ? 'klein' : ''}`}
        style={{ left: `calc(${links}% - ${breite / 2}cqw)`, bottom: `calc(${unten}% + 1.2cqw)`, width: `${breite}cqw` }}>
        <Medaille platz={platz} className="sb-medaille" />
        <div className={`sb-eb ${bild.art}`} style={{ backgroundImage: bildUrl(bild.url) }} />
        {/* Siegername nie kürzen: Umbruch nur zwischen Vor- und Nachname, Größe
            passt namenEinpassen() an (sonst vier Zeilen, die Karte stieß an den Titel) */}
        {platz === 1
          ? <div className="sb-en"><span>{p?.vorname || ''}</span>{p?.vorname && p?.nachname ? ' ' : ''}<span>{p?.nachname || ''}</span></div>
          : <div className="sb-en">{name(p)}</div>}
        <div className="sb-ev">{p.verein_name || ''}</div>
      </div>
    );
  };

  // Disziplin nicht doppelt: viele Kategorien heißen wie ihre Disziplin oder
  // beginnen damit („Continous Fighting“, „Rumble 7-9 Jahre“), sonst stünde
  // „Continous Fighting · Continous Fighting“ unter dem Titel.
  const disziplin = kategorie?.division_type || '';
  const katName = kategorie?.kategorie_name || '';
  const kat = disziplin && katName.toLowerCase().startsWith(disziplin.toLowerCase())
    ? katName : [disziplin, katName].filter(Boolean).join(' · ');

  return (
    <div className="sb-flaeche">
      <div className={`sb ${zweiDritte ? 'zwei-dritte' : ''}`} ref={buehneRef}
        style={{ backgroundImage: bildUrl(`${process.env.PUBLIC_URL || ''}/turniertag/buehne.jpg`) }}>
        <div className="sb-strahlen" />
        <div className="sb-licht" />
        <div className="sb-kopf">
          <div className="sb-titel">Siegerehrung</div>
          <div className="sb-kat">{kat}</div>
        </div>
        {total > 1 && <div className="sb-zaehler">{(idx ?? 0) + 1} / {total}</div>}
        <div className="sb-podest p2"><div className="sb-zahl">2</div></div>
        <div className="sb-podest p1"><div className="sb-zahl">1</div></div>
        <div className="sb-podest p3"><div className="sb-zahl">3</div></div>
        {erster && <div className="sb-sieger">Sieger</div>}

        {dritte.length === 1 && karte(dritte[0], 3, KARTE[3].mitte, KARTE[3].unten, KARTE[3].breite)}
        {zweiDritte && dritte.map((p, i) => karte(p, 3, 54.5 + (i === 0 ? -5.25 : 5.25), KARTE[3].unten, 10.2, true))}
        {zweiter && karte(zweiter, 2, KARTE[2].mitte, KARTE[2].unten, KARTE[2].breite)}
        {erster && karte(erster, 1, KARTE[1].mitte, KARTE[1].unten, KARTE[1].breite)}

        <div className={`sb-rechts ${zweiDritte ? 'eng' : ''}`}>
          <div className="sb-liste">
            {podium.map((p, i) => (
              <div key={i} className="sb-ez" data-p={p.platzierung}>
                <Medaille platz={p.platzierung} />
                <div className="sb-n">
                  {name(p)}
                  <small>{[p.verein_name, p.punkte != null ? `${punkteText(p.punkte)} Punkte` : ''].filter(Boolean).join(' · ')}</small>
                </div>
              </div>
            ))}
          </div>
          {restZeigen.length > 0 && (
            <div className="sb-rest">
              <div className="sb-rest-titel">Weitere Plätze</div>
              <div className="sb-rest-spalte">
                {spalteL.map((p, i) => <div key={i} className="sb-er"><b>{p.platzierung}.</b><span>{name(p)}</span></div>)}
                {restMehr > 0 && spalteR.length === 0 && <div className="sb-er"><b /><span>und {restMehr} weitere</span></div>}
              </div>
              <div className="sb-rest-spalte">
                {spalteR.map((p, i) => <div key={i} className="sb-er"><b>{p.platzierung}.</b><span>{name(p)}</span></div>)}
                {restMehr > 0 && spalteR.length > 0 && <div className="sb-er"><b /><span>und {restMehr} weitere</span></div>}
              </div>
            </div>
          )}
        </div>

        <div className="sb-blitz-gross" />
        {FUNKEN.map((f, i) => (
          <div key={i} className="sb-funke" style={{ left: `${f.links}%`, background: f.farbe, boxShadow: `0 0 .4cqw ${f.farbe}` }} />
        ))}
      </div>
    </div>
  );
}
